/** Training-video pop-up (Bunny Stream embed). The iframe exists only while open. */
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface Props {
  embedUrl: string;
  title: string;
  onClose: () => void;
}

export const VideoDialog: React.FC<Props> = ({ embedUrl, title, onClose }) => {
  const dialog = useRef<HTMLDialogElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const d = dialog.current;
    d?.showModal();
    closeBtn.current?.focus();
    return () => {
      if (d?.open) d.close();
    };
  }, []);

  // Close the native modal before unmounting so the page is no longer inert
  // when the caller refocuses "Watch video".
  const dismiss = () => {
    if (dialog.current?.open) dialog.current.close();
    onClose();
  };

  return (
    <dialog
      ref={dialog}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        dismiss();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
      className="m-auto w-[calc(100%-32px)] max-w-[960px] overflow-visible rounded bg-ink-deep p-0 text-[#F2F1EC] shadow-xl backdrop:bg-black/60"
    >
      <div className="p-3">
        <div className="mb-2 flex items-center justify-end gap-3">
          <button
            ref={closeBtn}
            type="button"
            aria-label="Close video"
            onClick={dismiss}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-white/10"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="aspect-video w-full" data-testid="from-precepi-video-player">
          <iframe
            className="h-full w-full"
            src={`${embedUrl}&autoplay=true`}
            title={title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </dialog>
  );
};

export default VideoDialog;
